import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useLocation, useRoute } from 'wouter';
import { AdminNavigation } from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import {
  Search,
  Save,
  Eye,
  Send,
  Plus,
  X,
  ChevronLeft,
  ArrowLeft,
  Undo2,
  Redo2,
  Tag,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  BadgeType,
  PRODUCT_BADGES,
  getBadgeConfig,
  CardDesign,
} from '@/components/flyer-themes';
import type { Product, Flyer, FlyerItem, FLYER_SECTIONS } from '@shared/schema';

// ============ TYPES ============

interface FlyerItemWithProduct extends FlyerItem {
  product: Product;
}

interface FlyerWithItems extends Flyer {
  items: FlyerItemWithProduct[];
}

type SectionType = 'produce' | 'grocery' | 'meat';

interface LayoutItem {
  productId: number;
  section: SectionType;
  displayOrder: number;
  flyerPrice: number | null;
  badge?: BadgeType;
  product: Product;
}

// Section grid configurations matching /instore display
const SECTION_CONFIG: Record<SectionType, { cols: number; rows: number; label: string; color: string }> = {
  produce: { cols: 9, rows: 2, label: 'Produce', color: 'bg-green-600' },
  grocery: { cols: 5, rows: 3, label: 'Groceries', color: 'bg-amber-500' },
  meat: { cols: 5, rows: 3, label: 'Meat', color: 'bg-red-600' },
};

// ============ DRAGGABLE PRODUCT THUMBNAIL (Sidebar Grid) ============

function DraggableProductThumbnail({ product }: { product: Product }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `sidebar-${product.id}`,
    data: { type: 'sidebar', product },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      title={`${product.description}${product.chineseName ? ` (${product.chineseName})` : ''}`}
      className={`relative aspect-square cursor-grab active:cursor-grabbing rounded overflow-hidden border-2 hover:border-emerald-400 transition-colors ${
        isDragging ? 'opacity-50 border-emerald-500' : 'border-transparent'
      }`}
    >
      {/* Product Image */}
      {product.imageUrl ? (
        <img
          src={product.imageUrl}
          alt={product.description}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-2xl">
          🛒
        </div>
      )}
      {/* Price overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5">
        <span className="text-white text-xs font-bold">${product.ourPrice?.toFixed(2)}</span>
      </div>
    </div>
  );
}

// ============ GRID CELL (Droppable) ============

function GridCell({
  section,
  index,
  item,
  onRemove,
  onUpdatePrice,
  onUpdateBadge,
  cardDesign = 'simple',
}: {
  section: SectionType;
  index: number;
  item?: LayoutItem;
  onRemove: (productId: number) => void;
  onUpdatePrice: (productId: number, price: number | null) => void;
  onUpdateBadge: (productId: number, badge: BadgeType | undefined) => void;
  cardDesign?: CardDesign;
}) {
  const cellId = `cell-${section}-${index}`;
  const { setNodeRef, isOver } = useDroppable({
    id: cellId,
    data: { type: 'cell', section, index },
  });

  // Also make the item draggable if it exists
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: item ? `grid-${item.productId}` : `empty-${cellId}`,
    data: item ? { type: 'grid', item, section, index } : undefined,
    disabled: !item,
  });

  // Inline price editing state
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [editPrice, setEditPrice] = useState('');

  const handlePriceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item) return;
    setEditPrice((item.flyerPrice ?? item.product.ourPrice)?.toFixed(2) || '');
    setIsEditingPrice(true);
  };

  const handlePriceSave = () => {
    if (!item) return;
    const newPrice = parseFloat(editPrice);
    if (!isNaN(newPrice) && newPrice >= 0) {
      // If same as original, clear custom price
      if (Math.abs(newPrice - (item.product.ourPrice || 0)) < 0.01) {
        onUpdatePrice(item.productId, null);
      } else {
        onUpdatePrice(item.productId, newPrice);
      }
    }
    setIsEditingPrice(false);
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePriceSave();
    } else if (e.key === 'Escape') {
      setIsEditingPrice(false);
    }
  };

  const badgeConfig = item?.badge ? getBadgeConfig(item.badge) : null;

  // Render action buttons inline
  const renderActionButtons = () => {
    if (!item) return null;
    return (
      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <Popover>
          <PopoverTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className={`bg-white/90 hover:bg-blue-100 rounded p-1 shadow ${badgeConfig ? 'ring-1 ring-blue-400' : ''}`}
            >
              <Tag className="w-3 h-3 text-blue-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-32 p-1" align="end">
            <div className="space-y-0.5">
              {PRODUCT_BADGES.map((badge) => (
                <button
                  key={badge.id}
                  onClick={() => onUpdateBadge(item.productId, item.badge === badge.id ? undefined : badge.id)}
                  className={`w-full text-left text-xs px-2 py-1 rounded flex items-center gap-1 ${
                    item.badge === badge.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-2 h-2 rounded ${badge.color}`} />
                  {badge.label}
                </button>
              ))}
              {item.badge && (
                <button
                  onClick={() => onUpdateBadge(item.productId, undefined)}
                  className="w-full text-left text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50"
                >
                  Remove badge
                </button>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(item.productId); }}
          className="bg-white/90 hover:bg-red-100 rounded p-1 shadow"
        >
          <X className="w-3 h-3 text-red-500" />
        </button>
      </div>
    );
  };

  // Render price display inline
  const renderPriceDisplay = () => {
    if (!item) return null;
    if (isEditingPrice) {
      return (
        <div className="flex items-center gap-0.5">
          <span className="text-sm text-red-600">$</span>
          <input
            type="number"
            step="0.01"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            onBlur={handlePriceSave}
            onKeyDown={handlePriceKeyDown}
            autoFocus
            className="w-14 text-sm font-bold text-red-600 border-b border-red-300 outline-none bg-transparent"
          />
        </div>
      );
    }
    return (
      <button
        onClick={handlePriceClick}
        className={`text-sm font-bold text-red-600 hover:underline cursor-pointer text-left ${
          item.flyerPrice ? 'bg-yellow-100 px-0.5 rounded' : ''
        }`}
        title="Click to edit price"
      >
        ${(item.flyerPrice ?? item.product.ourPrice)?.toFixed(2)}
        {item.flyerPrice && (
          <span className="text-xs text-gray-400 line-through ml-1">
            ${item.product.ourPrice?.toFixed(2)}
          </span>
        )}
      </button>
    );
  };

  // Render empty cell content
  const renderEmptyCell = () => (
    <div className="h-full flex flex-col items-center justify-center">
      {isOver ? (
        <div className="flex flex-col items-center text-emerald-700 animate-pulse">
          <Plus className="w-8 h-8" />
          <span className="text-xs font-bold">DROP HERE</span>
        </div>
      ) : (
        <Plus className="w-6 h-6 text-gray-300" />
      )}
    </div>
  );

  // ============ PHOTO DESIGN ============
  if (cardDesign === 'photo' && item) {
    return (
      <div
        ref={setNodeRef}
        className={`border-2 border-dashed rounded relative transition-all duration-150 ${
          isOver ? 'border-4 border-emerald-500 bg-emerald-200 ring-4 ring-emerald-400/50 scale-105 shadow-lg' : 'border-gray-300'
        } border-solid bg-white`}
        style={{ aspectRatio: '4/3' }}
      >
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          className={`h-full relative cursor-grab active:cursor-grabbing group ${isDragging ? 'opacity-50' : ''}`}
        >
          {/* Full bleed image */}
          {item.product.imageUrl ? (
            <img src={item.product.imageUrl} alt="" className="w-full h-full object-cover rounded" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl bg-gray-200 rounded">🛒</div>
          )}
          {renderActionButtons()}
          {/* Badge on image */}
          {badgeConfig && (
            <div className={`absolute top-0 left-0 ${badgeConfig.color} text-white text-[7px] font-bold px-1.5 py-0.5 rounded-br`}>
              {badgeConfig.label}
            </div>
          )}
          {/* Price overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 rounded-b">
            <p className="text-xs font-medium text-white truncate">{item.product.description}</p>
            <div className={`text-sm font-bold ${item.flyerPrice ? 'text-yellow-300' : 'text-white'}`}>
              ${(item.flyerPrice ?? item.product.ourPrice)?.toFixed(2)}
              {item.flyerPrice && (
                <span className="text-xs text-gray-300 line-through ml-1">
                  ${item.product.ourPrice?.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ BADGE DESIGN ============
  if (cardDesign === 'badge' && item) {
    return (
      <div
        ref={setNodeRef}
        className={`border-2 border-dashed rounded relative transition-all duration-150 ${
          isOver ? 'border-4 border-emerald-500 bg-emerald-200 ring-4 ring-emerald-400/50 scale-105 shadow-lg' : 'border-gray-300'
        } ${badgeConfig ? 'border-solid border-red-400' : 'border-solid'} bg-white`}
        style={{ aspectRatio: '4/3' }}
      >
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          className={`h-full flex flex-col cursor-grab active:cursor-grabbing group ${isDragging ? 'opacity-50' : ''}`}
        >
          {renderActionButtons()}
          {/* Large prominent badge banner */}
          {badgeConfig && (
            <div className={`${badgeConfig.color} text-white text-[8px] font-black px-2 py-1 text-center`}>
              ★ {badgeConfig.label} ★
            </div>
          )}
          {/* Compact image */}
          <div className="flex-1 relative bg-gray-100 overflow-hidden min-h-0">
            {item.product.imageUrl ? (
              <img src={item.product.imageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-200">🛒</div>
            )}
          </div>
          {/* Info with emphasized price */}
          <div className="p-1.5 bg-white text-center">
            <p className="text-xs font-medium text-gray-800 truncate">{item.product.description}</p>
            <div className="text-lg font-black text-red-600">
              ${(item.flyerPrice ?? item.product.ourPrice)?.toFixed(2)}
            </div>
            {item.flyerPrice && (
              <div className="text-[10px] text-gray-400 line-through">
                Reg. ${item.product.ourPrice?.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============ SIMPLE DESIGN (default) ============
  return (
    <div
      ref={setNodeRef}
      className={`border-2 border-dashed rounded relative transition-all duration-150 ${
        isOver
          ? 'border-4 border-emerald-500 bg-emerald-200 ring-4 ring-emerald-400/50 scale-105 shadow-lg'
          : 'border-gray-300'
      } ${item ? 'border-solid bg-white' : 'bg-gray-50'}`}
      style={{ aspectRatio: '4/3' }}
    >
      {item ? (
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          className={`h-full flex flex-col cursor-grab active:cursor-grabbing group ${isDragging ? 'opacity-50' : ''}`}
        >
          {/* Image */}
          <div className="flex-1 relative bg-gray-100 rounded-t overflow-hidden min-h-0">
            {item.product.imageUrl ? (
              <img src={item.product.imageUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl bg-gray-200">🛒</div>
            )}
            {renderActionButtons()}
            {/* Badge on image */}
            {badgeConfig && (
              <div className={`absolute top-0 left-0 ${badgeConfig.color} text-white text-[7px] font-bold px-1.5 py-0.5`}>
                {badgeConfig.label}
              </div>
            )}
            {/* Flyer price indicator */}
            {item.flyerPrice && (
              <div className="absolute bottom-1 left-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-1 rounded">
                SALE
              </div>
            )}
          </div>
          {/* Info */}
          <div className="p-1.5 bg-white rounded-b">
            <p className="text-xs font-medium text-gray-800 truncate">{item.product.description}</p>
            {renderPriceDisplay()}
          </div>
        </div>
      ) : renderEmptyCell()}
    </div>
  );
}

// ============ FLYER SECTION (Grid of Cells) ============

function FlyerSection({
  section,
  items,
  onRemove,
  onUpdatePrice,
  onUpdateBadge,
  cardDesign,
}: {
  section: SectionType;
  items: LayoutItem[];
  onRemove: (productId: number) => void;
  onUpdatePrice: (productId: number, price: number | null) => void;
  onUpdateBadge: (productId: number, badge: BadgeType | undefined) => void;
  cardDesign: CardDesign;
}) {
  const config = SECTION_CONFIG[section];
  const totalCells = config.cols * config.rows;

  // Create array of cells with items in correct positions
  const cells = Array.from({ length: totalCells }, (_, index) => {
    return items.find(item => item.displayOrder === index);
  });

  return (
    <div className="flex flex-col h-full">
      <div className={`${config.color} px-3 py-1 flex-shrink-0`}>
        <h3 className="text-white font-bold text-sm uppercase tracking-wide">
          {config.label} ({items.length}/{totalCells})
        </h3>
      </div>
      <div
        className="flex-1 grid gap-1 p-1 bg-gray-100"
        style={{
          gridTemplateColumns: `repeat(${config.cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${config.rows}, minmax(0, 1fr))`,
        }}
      >
        {cells.map((item, index) => (
          <GridCell
            key={index}
            section={section}
            index={index}
            item={item}
            onRemove={onRemove}
            onUpdatePrice={onUpdatePrice}
            onUpdateBadge={onUpdateBadge}
            cardDesign={cardDesign}
          />
        ))}
      </div>
    </div>
  );
}

// ============ DRAG OVERLAY ============

function DragOverlayContent({ product }: { product: Product }) {
  return (
    <div className="w-16 h-16 bg-white border-2 border-emerald-500 rounded shadow-2xl rotate-6 overflow-hidden">
      {product.imageUrl ? (
        <img src={product.imageUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xl">
          🛒
        </div>
      )}
    </div>
  );
}

// ============ MAIN EDITOR COMPONENT ============

export default function AdminFlyerEditor() {
  const [, navigate] = useLocation();
  const [, params] = useRoute('/admin/flyers/:id/edit');
  const flyerId = params?.id ? parseInt(params.id) : null;
  const queryClient = useQueryClient();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sectionFilter, setSectionFilter] = useState<SectionType | 'all'>('all');
  const [layoutItems, setLayoutItems] = useState<LayoutItem[]>([]);
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [cardDesign, setCardDesign] = useState<CardDesign>('simple');

  // Set page title
  useEffect(() => {
    document.title = 'Flyer Editor - Tops100';
  }, []);

  // Sensors for drag
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Undo/Redo system
  const MAX_HISTORY = 50;
  const historyRef = useRef<LayoutItem[][]>([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const isRestoringRef = useRef(false);

  const recordSnapshot = useCallback(() => {
    if (isRestoringRef.current) return;

    // Remove any redo states
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);

    // Add new snapshot (deep clone)
    historyRef.current.push(JSON.parse(JSON.stringify(layoutItems)));

    // Limit history size
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    } else {
      historyIndexRef.current++;
    }

    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, [layoutItems]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;

    isRestoringRef.current = true;
    historyIndexRef.current--;
    setLayoutItems(historyRef.current[historyIndexRef.current]);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(true);
    setHasChanges(true);

    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;

    isRestoringRef.current = true;
    historyIndexRef.current++;
    setLayoutItems(historyRef.current[historyIndexRef.current]);
    setCanUndo(true);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
    setHasChanges(true);

    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Initialize history with first snapshot when layout loads
  useEffect(() => {
    if (layoutItems.length > 0 && historyRef.current.length === 0) {
      historyRef.current.push(JSON.parse(JSON.stringify(layoutItems)));
      historyIndexRef.current = 0;
    }
  }, [layoutItems]);

  // Fetch flyer data
  const { data: flyer, isLoading: flyerLoading } = useQuery({
    queryKey: ['flyer', flyerId],
    queryFn: async () => {
      if (!flyerId) return null;
      return apiRequest<FlyerWithItems>(`/api/admin/flyers/${flyerId}`);
    },
    enabled: !!flyerId,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Initialize layout from flyer data
  useMemo(() => {
    if (flyer?.items && layoutItems.length === 0) {
      setLayoutItems(flyer.items.map(item => ({
        productId: item.productId,
        section: item.section as SectionType,
        displayOrder: item.displayOrder,
        flyerPrice: item.flyerPrice,
        product: item.product,
      })));
    }
  }, [flyer]);

  // Fetch available products
  const { data: availableProducts = [] } = useQuery({
    queryKey: ['available-products', flyerId, searchQuery, categoryFilter, sectionFilter],
    queryFn: async () => {
      if (!flyerId) return [];
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (sectionFilter !== 'all') params.append('section', sectionFilter);
      return apiRequest<Product[]>(`/api/admin/flyers/${flyerId}/available-products?${params}`);
    },
    enabled: !!flyerId,
    staleTime: 10000, // Cache for 10 seconds (shorter since filters change)
  });

  // Fetch categories - cache longer since these rarely change
  const { data: categories = [] } = useQuery({
    queryKey: ['flyer-categories'],
    queryFn: () => apiRequest<string[]>('/api/admin/flyers/categories'),
    staleTime: 300000, // Cache for 5 minutes
  });

  // Save layout mutation
  const saveMutation = useMutation({
    mutationFn: async (items: LayoutItem[]) => {
      return apiRequest('PUT', `/api/admin/flyers/${flyerId}/layout`, {
        items: items.map(item => ({
          productId: item.productId,
          section: item.section,
          displayOrder: item.displayOrder,
          flyerPrice: item.flyerPrice,
        })),
      });
    },
    onSuccess: () => {
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['flyer', flyerId] });
      queryClient.invalidateQueries({ queryKey: ['available-products'] });
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      // Save first
      await saveMutation.mutateAsync(layoutItems);
      return apiRequest('PATCH', `/api/admin/flyers/${flyerId}/publish`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyer', flyerId] });
      queryClient.invalidateQueries({ queryKey: ['admin-flyers'] });
    },
  });

  // Group items by section
  const sectionItems = useMemo(() => {
    return {
      produce: layoutItems.filter(i => i.section === 'produce'),
      grocery: layoutItems.filter(i => i.section === 'grocery'),
      meat: layoutItems.filter(i => i.section === 'meat'),
    };
  }, [layoutItems]);

  // Filter sidebar products (exclude those already in layout)
  const sidebarProducts = useMemo(() => {
    const usedIds = new Set(layoutItems.map(i => i.productId));
    return availableProducts.filter(p => !usedIds.has(p.id));
  }, [availableProducts, layoutItems]);

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current;
    if (data?.type === 'sidebar') {
      setActiveProduct(data.product);
    } else if (data?.type === 'grid') {
      setActiveProduct(data.item.product);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveProduct(null);
    const { active, over } = event;

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!overData || overData.type !== 'cell') return;

    const targetSection = overData.section as SectionType;
    const targetIndex = overData.index as number;

    if (activeData?.type === 'sidebar') {
      // Adding new item from sidebar
      const product = activeData.product as Product;

      // Check if cell is occupied
      const existingItem = layoutItems.find(
        i => i.section === targetSection && i.displayOrder === targetIndex
      );

      if (existingItem) {
        // Swap not supported from sidebar, just ignore
        return;
      }

      setLayoutItems(prev => [...prev, {
        productId: product.id,
        section: targetSection,
        displayOrder: targetIndex,
        flyerPrice: null,
        product,
      }]);
      setHasChanges(true);
      setTimeout(recordSnapshot, 0);
    } else if (activeData?.type === 'grid') {
      // Moving item within grid
      const movingItem = activeData.item as LayoutItem;
      const sourceSection = activeData.section as SectionType;
      const sourceIndex = activeData.index as number;

      if (sourceSection === targetSection && sourceIndex === targetIndex) {
        return; // Same cell
      }

      // Check if target is occupied
      const targetItem = layoutItems.find(
        i => i.section === targetSection && i.displayOrder === targetIndex
      );

      setLayoutItems(prev => {
        const newItems = prev.map(item => {
          if (item.productId === movingItem.productId) {
            // Move to new position
            return { ...item, section: targetSection, displayOrder: targetIndex };
          }
          if (targetItem && item.productId === targetItem.productId) {
            // Swap to source position
            return { ...item, section: sourceSection, displayOrder: sourceIndex };
          }
          return item;
        });
        return newItems;
      });
      setHasChanges(true);
      setTimeout(recordSnapshot, 0);
    }
  };

  const handleRemoveItem = (productId: number) => {
    setLayoutItems(prev => prev.filter(i => i.productId !== productId));
    setHasChanges(true);
    setTimeout(recordSnapshot, 0);
  };

  const handleUpdatePrice = (productId: number, flyerPrice: number | null) => {
    setLayoutItems(prev => prev.map(item =>
      item.productId === productId ? { ...item, flyerPrice } : item
    ));
    setHasChanges(true);
    setTimeout(recordSnapshot, 0);
  };

  const handleUpdateBadge = (productId: number, badge: BadgeType | undefined) => {
    setLayoutItems(prev => prev.map(item =>
      item.productId === productId ? { ...item, badge } : item
    ));
    setHasChanges(true);
    setTimeout(recordSnapshot, 0);
  };

  if (flyerLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <AdminNavigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (!flyer) {
    return (
      <div className="min-h-screen bg-gray-100">
        <AdminNavigation />
        <div className="flex flex-col items-center justify-center h-96 gap-4">
          <div className="text-xl text-gray-500">Flyer not found</div>
          <Button onClick={() => navigate('/admin/flyers')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Flyers
          </Button>
        </div>
      </div>
    );
  }

  const isDraft = flyer.status === 'draft';

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavigation />

      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/flyers')}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{flyer.title}</h1>
            <p className="text-sm text-gray-500">
              {new Date(flyer.weekStartDate).toLocaleDateString()} - {new Date(flyer.weekEndDate).toLocaleDateString()}
              <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                flyer.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                flyer.status === 'archived' ? 'bg-gray-100 text-gray-600' :
                'bg-amber-100 text-amber-700'
              }`}>
                {flyer.status.toUpperCase()}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-sm text-amber-600 mr-2">Unsaved changes</span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Cmd+Z)"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Cmd+Shift+Z)"
          >
            <Redo2 className="w-4 h-4" />
          </Button>
          <Select value={cardDesign} onValueChange={(v) => setCardDesign(v as CardDesign)}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue placeholder="Design" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="simple">Simple</SelectItem>
              <SelectItem value="photo">Photo</SelectItem>
              <SelectItem value="badge">Badge</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => window.open('/instore', '_blank')}
          >
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          {isDraft && (
            <>
              <Button
                variant="outline"
                onClick={() => saveMutation.mutate(layoutItems)}
                disabled={saveMutation.isPending}
              >
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
              >
                <Send className="w-4 h-4 mr-2" /> Publish
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Editor */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-[calc(100vh-8rem)]">
          {/* Sidebar - Product Picker (Tight Grid) */}
          <div className="w-72 bg-white border-r flex flex-col">
            <div className="p-2 border-b space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="flex gap-1">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="flex-1 h-8 text-xs">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sectionFilter} onValueChange={(v) => setSectionFilter(v as SectionType | 'all')}>
                  <SelectTrigger className="flex-1 h-8 text-xs">
                    <SelectValue placeholder="Section" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="produce">Produce</SelectItem>
                    <SelectItem value="grocery">Grocery</SelectItem>
                    <SelectItem value="meat">Meat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {sidebarProducts.length === 0 ? (
                <div className="text-center text-gray-400 py-8 text-sm">
                  No products available
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-1">
                  {sidebarProducts.map(product => (
                    <DraggableProductThumbnail key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Canvas */}
          <div className="flex-1 overflow-auto p-4">
            <div className="bg-gray-200 rounded-lg overflow-hidden min-h-full">
              {/* Produce Section - Top */}
              <div style={{ height: '40%' }}>
                <FlyerSection
                  section="produce"
                  items={sectionItems.produce}
                  onRemove={handleRemoveItem}
                  onUpdatePrice={handleUpdatePrice}
                  onUpdateBadge={handleUpdateBadge}
                  cardDesign={cardDesign}
                />
              </div>
              {/* Bottom Row - Grocery & Meat */}
              <div className="flex" style={{ height: '60%' }}>
                <div className="w-1/2">
                  <FlyerSection
                    section="grocery"
                    items={sectionItems.grocery}
                    onRemove={handleRemoveItem}
                    onUpdatePrice={handleUpdatePrice}
                    onUpdateBadge={handleUpdateBadge}
                    cardDesign={cardDesign}
                  />
                </div>
                <div className="w-1/2">
                  <FlyerSection
                    section="meat"
                    items={sectionItems.meat}
                    onRemove={handleRemoveItem}
                    onUpdatePrice={handleUpdatePrice}
                    onUpdateBadge={handleUpdateBadge}
                    cardDesign={cardDesign}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeProduct && <DragOverlayContent product={activeProduct} />}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
